import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Share2, Users, FlaskConical, ListTodo, GitMerge,
  Database, Settings as SettingsIcon, LogOut, ChevronDown, Menu, X, Shield, FolderTree, Search, Mic, Star,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GlobalSearch from "@/components/GlobalSearch";
import VoiceCommandModal from "@/components/VoiceCommandModal";

const NAV = [
  { to: "/app", end: true, label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/app/spider-contact", label: "Spider Contact", icon: Share2, testid: "nav-spider" },
  { to: "/app/entities", label: "Entity Files", icon: Users, testid: "nav-entities" },
  { to: "/app/groups", label: "Groups & Folders", icon: FolderTree, testid: "nav-groups" },
  { to: "/app/research", label: "Research & Evidence", icon: FlaskConical, testid: "nav-research" },
  { to: "/app/actions", label: "Actions", icon: ListTodo, testid: "nav-actions" },
  { to: "/app/review", label: "Review Queue", icon: GitMerge, testid: "nav-review" },
  { to: "/app/data", label: "Imports & Exports", icon: Database, testid: "nav-data" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppShell() {
  const { user, logout, workspaces, currentWorkspace, selectWorkspace, setDefaultWorkspace, role, workspaceId, canEdit } = useAuth();
  const navigate = useNavigate();
  const [wsOpen, setWsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [voiceCmdOpen, setVoiceCmdOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };

  const sidebar = (
    <aside className="flex flex-col h-full" style={{ width: 256, background: "var(--si-surface)", borderRight: "1px solid var(--si-border)" }}>
      <div className="px-5 py-5" style={{ borderBottom: "1px solid var(--si-border)" }}>
        <div className="font-display text-lg leading-tight" style={{ color: "var(--si-text)" }}>Strategic Interest</div>
        <div className="si-label mt-0.5">Spider Contact</div>
      </div>

      {/* Workspace switcher */}
      <div className="relative px-3 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
        <button data-testid="workspace-switcher" onClick={() => setWsOpen((o) => !o)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm transition-colors duration-200"
          style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2, color: "var(--si-text)" }}>
          <span className="truncate text-left">{currentWorkspace?.name || "Select workspace"}</span>
          <ChevronDown size={14} strokeWidth={1.5} />
        </button>
        {wsOpen && (
          <div className="absolute left-3 right-3 mt-1 z-50 py-1" style={{ background: "var(--si-surface-hover)", border: "1px solid var(--si-border)", borderRadius: 2 }}>
            {workspaces.map((w) => (
              <div key={w.id} className="flex items-center hover:bg-[var(--si-bg)]">
                <button data-testid={`workspace-opt-${w.id}`}
                  onClick={() => { selectWorkspace(w.id); setWsOpen(false); }}
                  className="flex-1 min-w-0 text-left px-3 py-2 text-sm transition-colors duration-200"
                  style={{ color: w.id === currentWorkspace?.id ? "var(--si-silver)" : "var(--si-text-2)" }}>
                  <span className="truncate">{w.name}</span>
                  <span className="si-label ml-2">{w.role}</span>
                  {w.is_default && <span className="si-label ml-1" style={{ color: "var(--si-teal-light)" }}>· default</span>}
                </button>
                <button data-testid={`workspace-default-${w.id}`} title={w.is_default ? "Default workspace" : "Set as default (opens here on every device)"}
                  onClick={(e) => { e.stopPropagation(); setDefaultWorkspace(w.id); }}
                  className="px-2.5 py-2 transition-colors duration-200"
                  style={{ color: w.is_default ? "var(--si-teal-light)" : "var(--si-text-2)" }}>
                  <Star size={14} strokeWidth={1.5} fill={w.is_default ? "currentColor" : "none"} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        <div className="px-3 pb-2 flex gap-2" style={{ borderBottom: "1px solid var(--si-border)" }}>
          <button data-testid="global-search-btn" onClick={() => setSearchOpen(true)}
            className="flex-1 flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-200"
            style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2, color: "var(--si-text-2)" }}>
            <Search size={15} strokeWidth={1.5} /> Search
          </button>
          {canEdit && (
            <button data-testid="voice-command-btn" onClick={() => setVoiceCmdOpen(true)} title="Voice command"
              className="flex items-center justify-center px-3 py-2 transition-colors duration-200"
              style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2, color: "var(--si-teal-light)" }}>
              <Mic size={15} strokeWidth={1.5} />
            </button>
          )}
        </div>
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-200"
            style={({ isActive }) => ({
              color: isActive ? "var(--si-text)" : "var(--si-text-2)",
              background: isActive ? "var(--si-surface-hover)" : "transparent",
              borderLeft: isActive ? "2px solid var(--si-teal-light)" : "2px solid transparent",
            })}>
            <n.icon size={17} strokeWidth={1.5} /> {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-5 py-4" style={{ borderTop: "1px solid var(--si-border)" }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center justify-center" style={{ width: 32, height: 32, background: "var(--si-teal)", borderRadius: 2 }}>
            <span className="text-xs font-mono text-white">{(user?.name || user?.email || "?")[0]?.toUpperCase()}</span>
          </div>
          <div className="min-w-0">
            <div className="text-sm truncate" style={{ color: "var(--si-text)" }}>{user?.name || user?.email}</div>
            <div className="si-label flex items-center gap-1"><Shield size={10} strokeWidth={1.5} />{role}</div>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={doLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-200"
          style={{ color: "var(--si-text-2)", border: "1px solid var(--si-border)", borderRadius: 2 }}>
          <LogOut size={15} strokeWidth={1.5} /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex app-viewport" style={{ overflow: "hidden", background: "var(--si-bg)" }}>
      <div className="hidden md:block h-full">{sidebar}</div>

      {/* Mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 h-full">
        <header className="md:hidden flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--si-border)", background: "var(--si-surface)", paddingTop: "max(0.75rem, env(safe-area-inset-top))", paddingLeft: "max(1rem, env(safe-area-inset-left))", paddingRight: "max(1rem, env(safe-area-inset-right))" }}>
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen((o) => !o)} style={{ color: "var(--si-text)" }}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span className="font-display" style={{ color: "var(--si-text)" }}>Strategic Interest</span>
          <span style={{ width: 22 }} />
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      {searchOpen && <GlobalSearch workspaceId={workspaceId} onClose={() => setSearchOpen(false)} />}
      {voiceCmdOpen && <VoiceCommandModal workspaceId={workspaceId} onClose={() => setVoiceCmdOpen(false)} />}
    </div>
  );
}
